import type { SerializedError } from '@reduxjs/toolkit'

import {
  errorView,
  queryView,
  type QueryResultLike,
  type QueryView,
  type TransportError,
} from '@krp/client-runtime'

import type { Freshness, NativeRead } from '../api/envelope'
import { formatDateTime } from './format'
import type { ErrorVm, FreshnessVm, SaveStateVm, SectionVm } from './types'

/** Результат мутации RTK Query в том виде, в каком его читают селекторы vm. */
export interface MutationLike<T = unknown> {
  data?: T
  error?: TransportError | SerializedError
  isUninitialized: boolean
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
}

export const IDLE_MUTATION: MutationLike = {
  isUninitialized: true,
  isLoading: false,
  isError: false,
  isSuccess: false,
}

/** Надпись свежести: слово и время, а не только цвет (CLAUDE.md §7). */
export function freshnessVm(freshness: Freshness): FreshnessVm {
  const when = freshness.observedAt === null ? null : formatDateTime(freshness.observedAt)
  switch (freshness.kind) {
    case 'fresh':
      return { kind: 'fresh', label: 'Актуально', detail: when === null ? null : `Прочитано из 1С: ${when}` }
    case 'stale': {
      const parts = [
        when === null ? 'Время чтения неизвестно' : `Прочитано из 1С: ${when}`,
        freshness.reason === null ? null : `Причина: ${freshness.reason}`,
      ]
      return { kind: 'stale', label: 'Устарело', detail: parts.filter((part) => part !== null).join('. ') }
    }
    case 'unknown':
      return {
        kind: 'unknown',
        label: 'Свежесть не сообщена',
        detail: 'Сервер не передал отметку чтения: не считайте эти данные актуальными без проверки',
      }
  }
}

/**
 * Раздел экрана из результата запроса: вид состояния, свежесть и строки.
 * Ошибка при ранее полученных данных — ошибка, а не старые данные как
 * актуальные (F06): строки есть только у состояния `ready`.
 */
export function sectionOf<T, R>(
  query: QueryResultLike<NativeRead<T>>,
  project: (data: T) => readonly R[],
  empty: readonly R[],
): SectionVm<R> {
  const view: QueryView = queryView(query, (read) => project(read.data).length === 0)
  const ready = view.status === 'ready' && query.data !== undefined
  return {
    query: view,
    freshness: ready && query.data !== undefined ? freshnessVm(query.data.freshness) : null,
    rows: ready && query.data !== undefined ? project(query.data.data) : empty,
  }
}

export function mutationState(mutation: MutationLike): SaveStateVm {
  return {
    busy: mutation.isLoading,
    error: mutation.isError && mutation.error !== undefined ? toSaveError(mutation.error) : null,
    saved: mutation.isSuccess,
  }
}

function toSaveError(error: TransportError | SerializedError): ErrorVm {
  const view = errorView(error)
  return { message: view.message, code: view.code, correlationId: view.correlationId }
}

/** Выполнялась ли команда с неизвестным исходом (ответа нет — повторять можно только теми же ключами). */
export function isUnknownOutcome(mutation: MutationLike): boolean {
  return mutation.isError && errorView(mutation.error).kind === 'unknown-outcome'
}

/**
 * Ревизия для команды: из ответа службы, а пока он не пришёл — из Platform
 * (`resourceVersion`, AU1b). Чтение службы ОТКАЗАЛО — ревизии нет: экран
 * показывает ошибку, и команда поверх неё не строится.
 */
export function commandRevision(
  resourceVersion: string | null | undefined,
  ...queries: readonly QueryResultLike<NativeRead<unknown>>[]
): string | null {
  const native = firstConfigurationVersion(...queries)
  if (native !== null) return native
  return queries.some((query) => query.isError) ? null : (resourceVersion ?? null)
}

/** Первая ревизия настроек базы, которую сообщил хотя бы один из ответов. */
export function firstConfigurationVersion(
  ...queries: readonly QueryResultLike<NativeRead<unknown>>[]
): string | null {
  for (const query of queries) {
    if (query.data !== undefined && !query.isError) return query.data.configurationVersion
  }
  return null
}
